import type { PlatformAccessGrant } from '@sentinel/shared';
import { Button, EmptyState, Panel, StateBadge, Table, type Column } from '@sentinel/ui';
import { formatDateTime, goToHost, QueryState } from '@sentinel/web-core';
import { useTranslation } from 'react-i18next';
import { RequestAccessForm } from './RequestAccessForm';
import { grantTone, isEnterable } from './grant-state';
import { useSupport } from './use-support';

const K = 'console.support';

/** Support windows (ADR 0011): ask an organization for access, then enter it on `control.` once
 *  its Admin approves; every request made inside is in the organization's audit. */
export function SupportPage() {
  const { t } = useTranslation();
  const { grants, organizations, request, close } = useSupport();
  const columns: Column<PlatformAccessGrant>[] = [
    {
      key: 'org',
      header: t(`${K}.columns.organization`),
      render: (g) => <b>{g.organizationName}</b>,
    },
    { key: 'reason', header: t(`${K}.columns.reason`), render: (g) => g.reason },
    {
      key: 'status',
      header: t(`${K}.columns.status`),
      render: (g) => <StateBadge tone={grantTone(g)} label={t(`${K}.statuses.${g.status}`)} />,
    },
    {
      key: 'until',
      header: t(`${K}.columns.until`),
      mono: true,
      render: (g) => (g.expiresAt === null ? '—' : formatDateTime(g.expiresAt)),
    },
    {
      key: 'act',
      header: '',
      render: (g) => (
        <span style={{ display: 'flex', gap: 6 }}>
          {isEnterable(g) ? (
            <Button size="sm" onClick={() => goToHost('control', { grantId: g.id })}>
              {t(`${K}.enter`)}
            </Button>
          ) : null}
          {g.status === 'approved' || g.status === 'pending' ? (
            <Button
              size="sm"
              variant="danger"
              disabled={close.isPending}
              onClick={() => close.mutate(g.id)}
            >
              {t(`${K}.close`)}
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <div className="split2">
      <Panel flush title={t(`${K}.title`)}>
        <QueryState query={grants}>
          {(data) =>
            data.items.length === 0 ? (
              <EmptyState title={t(`${K}.empty`)} description={t(`${K}.emptyHint`)} />
            ) : (
              <Table
                caption={t(`${K}.title`)}
                columns={columns}
                rows={data.items}
                rowKey={(g) => g.id}
              />
            )
          }
        </QueryState>
      </Panel>
      <RequestAccessForm
        organizations={organizations.data?.items ?? []}
        busy={request.isPending}
        onSubmit={(input) => request.mutate(input)}
      />
    </div>
  );
}
